import { diffSequence } from '@modules/documents/doc-engine/core/matcher';
import { TEXT_TOKEN_REGEX, isWholesaleRewrite, isWordText } from '@modules/documents/doc-engine/core/wordTokens';
import { MAX_WORD_DIFF_CELLS } from '../constants';

export type TWordDiffSegment = { text: string; change: 'equal' | 'insert' | 'delete' };

// A line break never bridges, so words replaced on both sides of it stay in their own paragraph.
const isSpace = (token: string) => /^[^\S\r\n]+$/u.test(token);

const getTableCells = (before: string[], after: string[]) => {
  const shortest = Math.min(before.length, after.length);
  let start = 0;
  while (start < shortest && before[start] === after[start]) start += 1;
  let end = 0;
  while (end < shortest - start && before[before.length - 1 - end] === after[after.length - 1 - end]) end += 1;
  return (before.length - start - end) * (after.length - start - end);
};

const replaceAll = (before: string, after: string): TWordDiffSegment[] => [
  ...(before ? [{ text: before, change: 'delete' as const }] : []),
  ...(after ? [{ text: after, change: 'insert' as const }] : []),
];

export const diffWordSegments = (before: string, after: string): TWordDiffSegment[] => {
  const oldTokens = before.match(TEXT_TOKEN_REGEX) ?? [];
  const newTokens = after.match(TEXT_TOKEN_REGEX) ?? [];
  if (getTableCells(oldTokens, newTokens) > MAX_WORD_DIFF_CELLS) return replaceAll(before, after);

  const ops = diffSequence(oldTokens, newTokens, (a, b) => a === b);
  if (isWholesaleRewrite(ops, isWordText)) return replaceAll(before, after);

  const segments: TWordDiffSegment[] = [];
  let deleted = '';
  let inserted = '';
  const flush = () => {
    if (deleted) segments.push({ text: deleted, change: 'delete' });
    if (inserted) segments.push({ text: inserted, change: 'insert' });
    deleted = '';
    inserted = '';
  };
  ops.forEach((op, index) => {
    const isBridge =
      op.type === 'equal' &&
      isSpace(op.currItem) &&
      [ops[index - 1], ops[index + 1]].every((neighbour) => neighbour && neighbour.type !== 'equal');
    if (op.type === 'delete') {
      deleted += op.prevItem;
    } else if (op.type === 'insert') {
      inserted += op.currItem;
    } else if (isBridge) {
      deleted += op.prevItem;
      inserted += op.currItem;
    } else {
      flush();
      const last = segments[segments.length - 1];
      if (last?.change === 'equal') last.text += op.currItem;
      else segments.push({ text: op.currItem, change: 'equal' });
    }
  });
  flush();
  return segments;
};
